import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Button } from '../components/primitives/index.js'
import InvoiceDocument from '../components/InvoiceDocument.jsx'
import SEO from '../components/SEO.jsx'
import { api } from '../lib/api.js'
import { useProducts } from '../context/ProductsContext.jsx'
import './InvoicePage.css'

/**
 * Mirrors Prod's /invoice contract: a token in the query string fetches the
 * order from the server (emailed invoice links); with no token it falls back
 * to navigate() state (the same post-checkout path OrderConfirmPage uses).
 * Mounted outside <Layout/> — no site nav/footer around the document.
 */
export default function InvoicePage() {
  const { state } = useLocation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { checkoutConfig, inventory, preorderMessage } = useProducts()

  const [fetchedOrder, setFetchedOrder] = useState(null)
  const [loading, setLoading] = useState(Boolean(token))
  const [fetchError, setFetchError] = useState(null)

  useEffect(() => {
    if (!token) return
    api
      .getInvoiceByToken(token)
      .then((data) => {
        setFetchedOrder(data)
        setLoading(false)
      })
      .catch((e) => {
        setFetchError(
          e.status === 410
            ? 'This invoice link has already been used. Contact support via Discord with your invoice number.'
            : (e.message ?? 'This invoice link is invalid or has expired.'),
        )
        setLoading(false)
      })
  }, [token])

  const order = state?.order ?? fetchedOrder

  const hasPreorder = order?.items?.some(
    (item) => item.preorder === true || inventory?.[item.lineId]?.preorder || inventory?.[`${item.productId ?? ''}__${item.variantId ?? ''}`]?.preorder,
  )

  if (loading) {
    return (
      <div className="invoice-page invoice-page--center">
        <SEO config={{ title: 'Invoice', robots: 'noindex, nofollow', titleRaw: true }} />
        <p>Loading invoice&hellip;</p>
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className="invoice-page invoice-page--center">
        <SEO config={{ title: 'Invoice', robots: 'noindex, nofollow', titleRaw: true }} />
        <span className="invoice-page__lock">&#128274;</span>
        <h1>Invoice Unavailable</h1>
        <p>{fetchError}</p>
        <Button to="/" variant="primary">
          Return Home
        </Button>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="invoice-page invoice-page--center">
        <SEO config={{ title: 'Invoice', robots: 'noindex, nofollow', titleRaw: true }} />
        <h1>No invoice found.</h1>
        <Button to="/" variant="primary">
          Return Home
        </Button>
      </div>
    )
  }

  return (
    <div className="invoice-page">
      <SEO config={{ title: `Invoice ${order.invoiceNumber}`, robots: 'noindex, nofollow', titleRaw: true }} />
      <InvoiceDocument order={order} activeConfig={checkoutConfig} isFresh={false} showDiscord={false} preorderMessage={hasPreorder ? preorderMessage : null} />
    </div>
  )
}
